import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconMail,
  IconMailForward,
  IconPaperclip,
  IconX,
} from '@tabler/icons-react';
import { useCountdown } from 'src/common/useCountdown';
import { buildMailtoUrl, truncateForMailto } from 'src/common/utils/email/mailto';
import { useConnectOutlook } from 'src/common/hooks/useConnectOutlook';
import { OutlookDisconnectButton } from 'src/common/components/OutlookDisconnectButton';
import { ApiError } from 'src/error/ApiError';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { EmailDraft, EmailDraftAttachment } from '../chat/types';
import { useOutlookEmailDraftActions } from '../chats/useOutlookEmailDraftActions';
import { EmailAttachmentPicker } from './EmailAttachmentPicker';

const MAILTO_MAX_BODY_LENGTH = 1800;
const MAILTO_HINT_DURATION_SECONDS = 20;

export type OutlookEmailContext = {
  enabled: boolean;
  connected: boolean;
  // True while Siesta is waiting on a connection to finish an email request, so connecting
  // from any card resumes the conversation rather than leaving the request stranded.
  connectRequested?: boolean;
  // Whether documents Siesta generated in this conversation may be offered as attachments.
  generatedAttachmentsEnabled?: boolean;
  conversationId?: string;
  clientId?: string;
  familyId?: string;
  pageSetsContentMeta?: boolean;
};

type Props = {
  draft: EmailDraft;
  outlook?: OutlookEmailContext;
  // True once the conversation has moved on past this draft (a newer user message exists).
  // Expired drafts render as a collapsed, muted row the user can expand again.
  expired?: boolean;
};

type FieldBlockProps = {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  emptyPlaceholder?: string;
  scrollable?: boolean;
};

function FieldBlock({
  label,
  value,
  copied,
  onCopy,
  emptyPlaceholder,
  scrollable,
}: FieldBlockProps) {
  const isEmpty = !value;
  return (
    <Paper bg="white" p="sm" radius="md" pos="relative">
      <Group justify="space-between" align="center" gap="xs">
        <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.7">
          {label}
        </Text>
        {!isEmpty && (
          <Button
            variant="subtle"
            color={copied ? 'teal' : 'gray'}
            size="compact-xs"
            leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
            onClick={onCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </Group>
      {isEmpty ? (
        <Text size="textSm" c="gray.6">
          {emptyPlaceholder}
        </Text>
      ) : (
        <Text
          size="textSm"
          c="gray.9"
          mah={scrollable ? 220 : undefined}
          style={{
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            overflowY: scrollable ? 'auto' : undefined,
          }}
        >
          {value}
        </Text>
      )}
    </Paper>
  );
}

export function EmailDraftCard({ draft, outlook, expired }: Props) {
  const clipboard = useClipboard({ timeout: 2000 });
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showHint, setShowHint] = useState(false);
  const hintCountdown = useCountdown(MAILTO_HINT_DURATION_SECONDS, showHint, () =>
    setShowHint(false),
  );

  const [attachment, setAttachment] = useState<EmailDraftAttachment | null>(
    draft.attachment ?? null,
  );
  const [pickerOpened, setPickerOpened] = useState(false);
  const [localSentAt, setLocalSentAt] = useState<string | null>(null);
  const [localSavedAt, setLocalSavedAt] = useState<string | null>(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // null = follow the expired default; true/false = the user's explicit choice, which is
  // reset whenever expiry changes so a newly expired card collapses even if it was open.
  const [collapsedOverride, setCollapsedOverride] = useState<boolean | null>(null);
  useEffect(() => {
    setCollapsedOverride(null);
  }, [expired]);
  const collapsed = collapsedOverride ?? !!expired;

  const { connectOutlook, isConnecting: isStartingOAuth } = useConnectOutlook();
  const {
    sendViaOutlook,
    saveToOutlookDrafts,
    updateAttachment,
    isSending,
    isSaving,
    isUpdatingAttachment,
  } = useOutlookEmailDraftActions(outlook?.conversationId);

  const sentAt = localSentAt ?? draft.sentAt ?? null;
  const savedAt = localSavedAt ?? draft.savedToOutlookAt ?? null;
  const outlookActive = !!outlook?.enabled && outlook.connected;
  const bodyTooLong = draft.body.length > MAILTO_MAX_BODY_LENGTH;

  const handleCopy = (field: string, value: string) => {
    clipboard.copy(value);
    setCopiedField(field);
  };

  const handleOpenInClient = () => {
    const url = buildMailtoUrl(draft.to, draft.subject, truncateForMailto(draft.body));
    window.open(url, '_self');
    setShowHint(true);
  };

  const handleConnect = () =>
    connectOutlook(outlook?.conversationId, {
      pageSetsContentMeta: outlook?.pageSetsContentMeta,
      autoResume: outlook?.connectRequested,
    });

  const handleActionError = (error: unknown, failedActionTitle: string) => {
    if (error instanceof ApiError && (error.status === 424 || error.status === 403)) {
      setNeedsReconnect(true);
      return;
    }
    if (error instanceof ApiError && error.status === 409) {
      // Already sent elsewhere (another tab, or a stale rehydrated card) — show the sent
      // state instead of an error; the next rehydrate replaces this with the real timestamp.
      setLocalSentAt(new Date().toISOString());
      return;
    }
    if (error instanceof ApiError && error.status === 413) {
      setActionError('The attachment is too large to send (over 3 MB) — remove it and try again.');
      return;
    }
    notifyAndLogError(failedActionTitle, 'Something went wrong. Please try again.', error);
  };

  // The choice is persisted so a revision Siesta drafts next inherits it and a reopened
  // conversation shows it; the card updates first and rolls back if the server refuses.
  const changeAttachment = async (next: EmailDraftAttachment | null) => {
    const previous = attachment;
    setAttachment(next);
    setActionError(null);
    const attachmentFileId = next ? next.fileId : null;
    try {
      await updateAttachment({ draftId: draft.draftId, attachmentFileId });
    } catch (error) {
      setAttachment(previous);
      if (error instanceof ApiError && error.status === 413) {
        setActionError(
          'That document is over the 3 MB attachment limit, so it cannot be attached.',
        );
        return;
      }
      handleActionError(error, 'Could not update the attachment');
    }
  };

  const handleSend = async () => {
    setActionError(null);
    // Hoisted out of the try blocks below: the React Compiler cannot yet optimise value
    // blocks (optional chaining, nullish coalescing) inside try/catch.
    const attachmentFileId = attachment ? attachment.fileId : null;
    try {
      const updated = await sendViaOutlook({ draftId: draft.draftId, attachmentFileId });
      if (updated.sentAt) {
        setLocalSentAt(updated.sentAt);
      } else {
        setLocalSentAt(new Date().toISOString());
      }
    } catch (error) {
      handleActionError(error, 'Could not send the email');
    }
  };

  const handleSaveToDrafts = async () => {
    setActionError(null);
    const attachmentFileId = attachment ? attachment.fileId : null;
    try {
      const updated = await saveToOutlookDrafts({ draftId: draft.draftId, attachmentFileId });
      if (updated.savedToOutlookAt) {
        setLocalSavedAt(updated.savedToOutlookAt);
      } else {
        setLocalSavedAt(new Date().toISOString());
      }
    } catch (error) {
      handleActionError(error, 'Could not save the draft to Outlook');
    }
  };

  const fieldCopied = (field: string) => copiedField === field && clipboard.copied;

  const renderAttachmentRow = () => (
    <Group gap="xs" align="center">
      {attachment ? (
        <Badge
          variant="light"
          color="orange"
          size="lg"
          radius="sm"
          leftSection={<IconPaperclip size={12} />}
          rightSection={
            <ActionIcon
              variant="transparent"
              color="orange"
              size="xs"
              onClick={() => changeAttachment(null)}
              disabled={isUpdatingAttachment}
              aria-label="Remove attachment"
            >
              <IconX size={12} />
            </ActionIcon>
          }
          style={{ textTransform: 'none', maxWidth: '100%' }}
        >
          {attachment.fileName}
        </Badge>
      ) : null}
      <Button
        variant="subtle"
        color="orange"
        size="compact-xs"
        leftSection={<IconPaperclip size={12} />}
        onClick={() => setPickerOpened(true)}
        loading={isUpdatingAttachment}
      >
        {attachment ? 'Change attachment' : 'Add attachment'}
      </Button>
    </Group>
  );

  const renderOutlookActions = () => (
    <>
      {renderAttachmentRow()}
      <Group gap="xs">
        <Button
          variant="filled"
          color="orange"
          size="sm"
          leftSection={<IconMailForward size={14} />}
          onClick={handleSend}
          loading={isSending}
          disabled={!draft.to || isSaving}
        >
          Send via Outlook
        </Button>
        {savedAt ? (
          <Button
            variant="light"
            color="teal"
            size="sm"
            leftSection={<IconCheck size={14} />}
            disabled
          >
            Saved to Outlook drafts
          </Button>
        ) : (
          <Button
            variant="light"
            color="orange"
            size="sm"
            onClick={handleSaveToDrafts}
            loading={isSaving}
            disabled={isSending}
          >
            Save to Outlook drafts
          </Button>
        )}
      </Group>
      {!draft.to && (
        <Text size="textXs" c="gray.7">
          No recipient — save it to your Outlook drafts and add one there.
        </Text>
      )}
      {savedAt && !sentAt && (
        <Text size="textXs" c="gray.7">
          Finish and send it from Outlook, or send it directly from here.
        </Text>
      )}
      {actionError && (
        <Text size="textXs" c="red.7">
          {actionError}
        </Text>
      )}
      <Group gap="xs">
        <OutlookDisconnectButton />
      </Group>
    </>
  );

  const renderUnconnectedActions = () => (
    <>
      <Group gap="xs">
        <Button
          variant="filled"
          color="orange"
          size="sm"
          leftSection={<IconMail size={14} />}
          onClick={handleOpenInClient}
          disabled={bodyTooLong}
        >
          Open in email client
        </Button>
        {outlook?.enabled && (
          <Button
            variant="light"
            color="orange"
            size="sm"
            onClick={handleConnect}
            loading={isStartingOAuth}
          >
            Connect Outlook
          </Button>
        )}
      </Group>
      {bodyTooLong && (
        <Text size="textXs" c="gray.7">
          This draft is too long to open in your email client — use the copy buttons to move it over
          field by field.
        </Text>
      )}
      {showHint && hintCountdown > 0 && (
        <Text size="textXs" c="gray.7">
          Your email app should now be opening with this draft. If nothing appears, use the copy
          buttons to paste the content into your email instead.
        </Text>
      )}
    </>
  );

  const renderActionArea = () => {
    if (sentAt) {
      return (
        attachment && (
          <Text size="textXs" c="gray.7" style={{ overflowWrap: 'anywhere' }}>
            <IconPaperclip size={12} style={{ verticalAlign: 'text-bottom' }} aria-hidden />{' '}
            {attachment.fileName} was attached.
          </Text>
        )
      );
    }
    if (needsReconnect) {
      return (
        <>
          <Text size="textXs" c="red.7">
            Your Outlook connection has expired or is missing a permission. Reconnect to continue.
          </Text>
          <Button
            variant="light"
            color="orange"
            size="sm"
            onClick={handleConnect}
            loading={isStartingOAuth}
            style={{ alignSelf: 'flex-start' }}
          >
            Reconnect Outlook
          </Button>
        </>
      );
    }
    return outlookActive ? renderOutlookActions() : renderUnconnectedActions();
  };

  if (collapsed) {
    return (
      <UnstyledButton
        onClick={() => setCollapsedOverride(false)}
        aria-label="Expand email draft"
        p="sm"
        bg="gray.1"
        style={{ borderRadius: 'var(--mantine-radius-md)', width: '100%' }}
      >
        <Group gap="xs" align="center" justify="space-between" wrap="nowrap">
          <Group gap="xs" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            <IconMail size={14} stroke={1.5} color="var(--mantine-color-gray-6)" aria-hidden />
            <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.7" style={{ flexShrink: 0 }}>
              Email draft
            </Text>
            <Text size="textXs" c="gray.6" truncate>
              {draft.subject}
            </Text>
          </Group>
          <Group gap="xs" align="center" wrap="nowrap">
            {sentAt && (
              <Badge variant="light" color="teal" size="sm" leftSection={<IconCheck size={10} />}>
                Sent
              </Badge>
            )}
            <IconChevronDown size={14} color="var(--mantine-color-gray-6)" aria-hidden />
          </Group>
        </Group>
      </UnstyledButton>
    );
  }

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" justify="space-between">
          <Group gap="xs" align="center">
            <IconMail size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
              Email draft
            </Text>
          </Group>
          <Group gap="xs" align="center">
            {sentAt && (
              <Badge variant="light" color="teal" leftSection={<IconCheck size={12} />}>
                Sent via Outlook
              </Badge>
            )}
            {expired && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setCollapsedOverride(true)}
                aria-label="Collapse email draft"
              >
                <IconChevronUp size={14} />
              </ActionIcon>
            )}
          </Group>
        </Group>

        <Stack gap="xs">
          <FieldBlock
            label="To"
            value={draft.to}
            copied={fieldCopied('to')}
            onCopy={() => handleCopy('to', draft.to)}
            emptyPlaceholder="No recipient — add one in your email client"
          />
          <FieldBlock
            label="Subject"
            value={draft.subject}
            copied={fieldCopied('subject')}
            onCopy={() => handleCopy('subject', draft.subject)}
          />
          <FieldBlock
            label="Body"
            value={draft.body}
            copied={fieldCopied('body')}
            onCopy={() => handleCopy('body', draft.body)}
            scrollable
          />
        </Stack>

        {renderActionArea()}
      </Flex>

      {outlookActive && (
        <EmailAttachmentPicker
          opened={pickerOpened}
          onClose={() => setPickerOpened(false)}
          onSelect={changeAttachment}
          clientId={outlook?.clientId}
          familyId={outlook?.familyId}
          conversationId={outlook?.generatedAttachmentsEnabled ? outlook.conversationId : undefined}
        />
      )}
    </Box>
  );
}
